import { useEffect, useState } from 'react';
import { C } from '@ridgewill/brand';
import { ApiError, getBirthDate, setBirthDate, type BirthDateInfo } from '../lib/api.js';
import { LOGIN_URL } from '../lib/auth-client.js';
import { Modal } from '../components/Modal.js';
import { Select } from '../components/Select.js';
import { Note, Problem, button } from '../components/form.js';
import {
  BIRTH_DATE_SUPPORT_PATH,
  EMPTY_BIRTH_DATE,
  MONTH_OPTIONS,
  dayOptions,
  fitDay,
  formatBirthDate,
  fromIsoDate,
  isAdultToMinor,
  readback,
  yearOptions,
  type BirthDateValue,
} from '../lib/birth-date.js';

/**
 * Date of birth on Profile (RidgeWill/auth#672).
 *
 * The section shows the date on file and opens a modal to change it, or to add
 * one when there is none. Every rule is auth's: the 24-hour window for moving
 * to 18 or older, the Family Group refusal, the under-13 freeze. The modal
 * reads auth's refusal codes; it never decides one itself, except to ask "are
 * you sure" before an adult becomes under 18. It does not tell a member when
 * the window closes (Jon, 2026-10-30).
 *
 * Copy is the issue's "Proposed copy", trimmed by Jon on 2026-10-30. No em dashes.
 */

export const COPY = {
  confirm:
    'This date makes you under 18. Protections for members under 18 will apply to your account right away, and some apps may ask a parent or guardian to accept their terms. Continue?',
  needsSupport:
    "You can't change your date of birth to 18 or older here. Send us a support request with a photo of one of these: a state-issued ID or driver's license, a passport, or a birth certificate. We'll check it, update your date of birth, and then delete the photo.",
  familyGroup:
    "You are a guardian in a Family Group, so your date of birth can't be changed to under 18 here. If your date of birth is wrong, send us a support request and we'll help.",
  saved: "Your date of birth is updated. Apps you're signed in to will see the change within 15 minutes.",
  frozenFallback:
    'Your account is paused. Sign in again to see what happens next and to correct your date of birth if it is wrong.',
  invalidFallback: 'That date could not be saved. Check it and try again.',
} as const;

/** Where a frozen account goes: the hosted sign-in, coming back to Profile. */
const SIGN_IN_AGAIN = `${LOGIN_URL}/login?returnTo=account&at=${encodeURIComponent('/')}`;

export function BirthDate({ now = () => new Date() }: { now?: () => Date } = {}) {
  const [info, setInfo] = useState<BirthDateInfo | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  /** Bumped on every open, so each opening starts on a fresh first step. */
  const [opening, setOpening] = useState(0);

  async function load() {
    try {
      setInfo(await getBirthDate());
      setLoadError(null);
    } catch (e) {
      setLoadError(e instanceof Error ? e.message : 'Your date of birth could not be loaded.');
    }
  }

  useEffect(() => {
    void load();
  }, []);

  const onFile = formatBirthDate(info?.dateOfBirth);

  return (
    <div style={{ marginTop: '2rem', paddingTop: '1.5rem', borderTop: `1px solid ${C.border}` }}>
      <h3 id="birth-date-heading" style={{ fontSize: '1rem', margin: '0 0 .35rem' }}>
        Date of birth
      </h3>
      {loadError && !info ? (
        <Problem>{loadError}</Problem>
      ) : !info ? (
        <Note>Loading…</Note>
      ) : (
        <div style={row}>
          <span style={value}>{onFile ?? <em style={{ color: C.textMid }}>Not added yet</em>}</span>
          <button
            type="button"
            onClick={() => {
              setOpening((n) => n + 1);
              setOpen(true);
            }}
            style={{ ...button, marginTop: 0, padding: '.45rem .9rem' }}
            aria-label={onFile ? 'Change date of birth' : 'Add date of birth'}
          >
            {onFile ? 'Change' : 'Add'}
          </button>
        </div>
      )}
      {info && (
        <BirthDateModal
          // A fresh modal each time it opens, so it does not reopen on its last
          // step or with last time's refusal. Not keyed on `open` itself: that
          // would remount on close and cut off the exit animation.
          key={opening}
          open={open}
          info={info}
          now={now}
          onClose={() => setOpen(false)}
          onSaved={() => void load()}
        />
      )}
    </div>
  );
}

type Step =
  | { kind: 'edit' }
  | { kind: 'confirm'; iso: string; text: string }
  | { kind: 'saved' }
  | { kind: 'frozen'; message: string };

type Refusal = { kind: 'needs-support' } | { kind: 'family-group' } | { kind: 'message'; text: string };

export function BirthDateModal({
  open,
  info,
  now,
  onClose,
  onSaved,
}: {
  open: boolean;
  info: BirthDateInfo;
  now: () => Date;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [value, setValue] = useState<BirthDateValue>(() =>
    info.dateOfBirth ? fromIsoDate(info.dateOfBirth) : EMPTY_BIRTH_DATE,
  );
  const [step, setStep] = useState<Step>({ kind: 'edit' });
  const [busy, setBusy] = useState(false);
  const [refusal, setRefusal] = useState<Refusal | null>(null);

  const today = now();
  const said = readback(value, today);
  const unchanged = said.kind === 'ok' && said.iso === info.dateOfBirth;
  const canSave = said.kind === 'ok' && !unchanged && !busy;

  async function save(iso: string) {
    setBusy(true);
    setRefusal(null);
    try {
      await setBirthDate(iso);
      setStep({ kind: 'saved' });
      onSaved();
    } catch (e) {
      const code = e instanceof ApiError ? e.code : undefined;
      const message = e instanceof Error && e.message.trim() ? e.message : null;
      if (code === 'ACCOUNT_FROZEN') {
        setStep({ kind: 'frozen', message: message ?? COPY.frozenFallback });
      } else {
        setStep({ kind: 'edit' });
        if (code === 'BIRTH_DATE_NEEDS_SUPPORT') setRefusal({ kind: 'needs-support' });
        else if (code === 'BIRTH_DATE_FAMILY_GROUP') setRefusal({ kind: 'family-group' });
        else if (code === 'BIRTH_DATE_INVALID') setRefusal({ kind: 'message', text: message ?? COPY.invalidFallback });
        else setRefusal({ kind: 'message', text: message ?? 'That could not be saved. Try again in a moment.' });
      }
    } finally {
      setBusy(false);
    }
  }

  function submit() {
    if (said.kind !== 'ok' || !canSave) return;
    if (isAdultToMinor(info, said.age, today)) {
      setStep({ kind: 'confirm', iso: said.iso, text: said.text });
      return;
    }
    void save(said.iso);
  }

  // The session is gone once an account is frozen, so every way out of the
  // dialog goes to sign in again rather than back to a page that cannot load.
  const close = step.kind === 'frozen' ? () => window.location.assign(SIGN_IN_AGAIN) : onClose;

  return (
    <Modal open={open} title="Change your date of birth" onClose={close}>
      {step.kind === 'saved' ? (
        <>
          <p role="status" style={para}>
            {COPY.saved}
          </p>
          <div style={actions}>
            <button type="button" onClick={onClose} style={primary(false)}>
              Done
            </button>
          </div>
        </>
      ) : step.kind === 'frozen' ? (
        <>
          <p role="alert" style={para}>
            {step.message}
          </p>
          <div style={actions}>
            <a href={SIGN_IN_AGAIN} style={{ ...primary(false), textDecoration: 'none', display: 'inline-block' }}>
              Sign in again
            </a>
          </div>
        </>
      ) : step.kind === 'confirm' ? (
        <>
          <p style={{ ...para, fontWeight: 600 }}>{step.text}</p>
          <p style={para}>{COPY.confirm}</p>
          <div style={actions}>
            <button type="button" disabled={busy} onClick={() => void save(step.iso)} style={primary(busy)}>
              {busy ? 'Saving…' : 'Continue'}
            </button>
            <button type="button" disabled={busy} onClick={() => setStep({ kind: 'edit' })} style={{ ...button, marginTop: 0 }}>
              Go back
            </button>
          </div>
        </>
      ) : (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            submit();
          }}
        >
          <BirthDateFields
            value={value}
            today={today}
            onChange={(v) => {
              setValue(v);
              setRefusal(null);
            }}
          />
          {/* Only a date that cannot be saved says anything here (Jon, 2026-10-30). */}
          <div aria-live="polite">
            {said.kind === 'invalid' && <p style={{ ...para, margin: '-.5rem 0 .75rem', color: C.dangerText }}>{said.text}</p>}
          </div>
          {refusal && <RefusalMessage refusal={refusal} />}
          <div style={{ ...actions, marginTop: '1rem' }}>
            <button type="submit" disabled={!canSave} style={primary(!canSave)}>
              {busy ? 'Saving…' : 'Save'}
            </button>
            <button type="button" onClick={onClose} disabled={busy} style={{ ...button, marginTop: 0 }}>
              Cancel
            </button>
          </div>
        </form>
      )}
    </Modal>
  );
}

function RefusalMessage({ refusal }: { refusal: Refusal }) {
  if (refusal.kind === 'message') return <Problem>{refusal.text}</Problem>;
  return (
    <div role="alert" style={refusalBox}>
      <p style={{ ...para, margin: 0 }}>{refusal.kind === 'needs-support' ? COPY.needsSupport : COPY.familyGroup}</p>
      {/* A full page load, like every link in this app (see App.tsx). The form
          opens with the date-of-birth topic already chosen. */}
      <a href={BIRTH_DATE_SUPPORT_PATH} style={{ color: C.brand, display: 'inline-block', marginTop: '.5rem' }}>
        Contact support
      </a>
    </div>
  );
}

/**
 * Month, Day and Year, laid out as the login app's `BirthDateFields`
 * (RidgeWill/auth, apps/login). All three are the portal's own dropdown, so
 * nothing can be mistyped (Jon, 2026-10-30): Day lists only the days the month
 * has, and Year runs from this year back 120 years. Each starts on a muted
 * placeholder, never on January, 1 or this year.
 */
export function BirthDateFields({
  value,
  today,
  onChange,
}: {
  value: BirthDateValue;
  today: Date;
  onChange: (v: BirthDateValue) => void;
}) {
  const set = (key: keyof BirthDateValue) => (next: string) => onChange(fitDay({ ...value, [key]: next }));
  return (
    <fieldset style={{ border: 0, padding: 0, margin: '0 0 1rem', minWidth: 0 }}>
      {/* For screen readers only: the dialog title already says it (Jon, 2026-10-30). */}
      <legend style={visuallyHidden}>Date of birth</legend>
      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1.3fr', gap: '.5rem' }}>
        <span style={part}>
          <span id="bday-month-label">Month</span>
          <Select
            value={value.month}
            options={MONTH_OPTIONS}
            onChange={set('month')}
            labelledBy="bday-month-label"
            placeholder="Month"
            size="lg"
            block
          />
        </span>
        <span style={part}>
          <span id="bday-day-label">Day</span>
          <Select
            value={value.day}
            options={dayOptions(value.month, value.year)}
            onChange={set('day')}
            labelledBy="bday-day-label"
            placeholder="Day"
            size="lg"
            block
          />
        </span>
        <span style={part}>
          <span id="bday-year-label">Year</span>
          <Select
            value={value.year}
            options={yearOptions(today)}
            onChange={set('year')}
            labelledBy="bday-year-label"
            placeholder="Year"
            size="lg"
            block
          />
        </span>
      </div>
    </fieldset>
  );
}

const visuallyHidden: React.CSSProperties = {
  position: 'absolute',
  width: 1,
  height: 1,
  padding: 0,
  margin: -1,
  overflow: 'hidden',
  clip: 'rect(0 0 0 0)',
  whiteSpace: 'nowrap',
  border: 0,
};
const row: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: '.75rem', flexWrap: 'wrap', marginTop: '.75rem' };
const value: React.CSSProperties = { flex: '1 1 12rem', fontSize: '1rem' };
const para: React.CSSProperties = { margin: '0 0 .75rem', lineHeight: 1.5 };
const actions: React.CSSProperties = { display: 'flex', gap: '.6rem', alignItems: 'center', flexWrap: 'wrap' };
const part: React.CSSProperties = { display: 'grid', gap: '.25rem', fontSize: '.85rem', color: C.textMid, minWidth: 0 };
const refusalBox: React.CSSProperties = {
  margin: '.75rem 0 0',
  padding: '.75rem .9rem',
  borderRadius: 6,
  background: C.warnBg,
  color: C.warnText,
  fontSize: '.92rem',
};
const primary = (disabled: boolean): React.CSSProperties => ({
  ...button,
  marginTop: 0,
  background: disabled ? 'transparent' : C.brand,
  color: disabled ? C.textMid : '#fff',
  borderColor: disabled ? C.inputBorder : C.brand,
  cursor: disabled ? 'default' : 'pointer',
});
